import { z } from "zod";

/**
 * Shared DTO cho Join Requests — POST /v1/groups/:id/join-requests
 * (US-E4.F4-0025 / @UC0014 — Request to Join via QR/Link).
 *
 * Một nguồn sự thật cho api (validate biên + shape response) và mobile (gọi + map), tránh drift
 * (DRY). C5: schema validate tại biên tái dùng được.
 *
 * Cross-check API_BUILD_PLAN [đã xoá → TDD §26] Phase 3 (Groups + RBAC + Invitations/Join): join-requests thuộc
 * `modules/invitations/{...,join-requests.service}`, endpoint `POST /v1/groups/:id/join-requests`.
 * Slice 0025 = NGƯỜI NGOÀI xin vào nhóm qua QR/link: tạo JoinRequest pending. Duyệt/từ chối =
 * story 0026 (Admin) — NGOÀI phạm vi.
 *
 * KHÁC accept-invite (0024 — owner/admin chủ động mời, token email-bound, accept = vào ngay):
 *  - join-request là NGƯỜI NGOÀI tự xin vào → KHÔNG vào ngay, chỉ tạo request pending chờ Admin duyệt.
 *  - Caller (UserAuthGuard) KHÔNG cần là member (người ngoài). userId lấy từ token, groupId từ path.
 *  - token (link/QR) là TÙY CHỌN: khi có, dùng để phát hiện link bị thu hồi/hết hiệu lực (AC4).
 *
 * ⚠️ no-SMS (C1): hoàn toàn qua QR/link. KHÔNG path SMS ở bất kỳ tầng nào.
 * BẢO MẬT (C2): response KHÔNG mang token raw / email / secret. `.strict()`.
 */

/**
 * Độ dài tối đa token thô (link/QR) tại biên (chặn over-post/DoS). Token issue() = 32 byte base64url
 * (~43 ký tự); dư tới 256 cho dung sai. Token quá dài → reject 400 (KHÔNG chạm DB).
 */
export const JOIN_REQUEST_TOKEN_MAX_LENGTH = 256;

/**
 * Trạng thái JoinRequest (khớp schema.prisma JoinRequest.status). Một nguồn sự thật cho api + test.
 * Slice 0025 tạo 'pending'; approve/reject (story 0026 — Admin) chuyển trạng thái sau.
 */
export const JOIN_REQUEST_STATUS = {
  PENDING: "pending",
  APPROVED: "approved",
  REJECTED: "rejected",
} as const;

export const joinRequestStatusSchema = z.enum([
  JOIN_REQUEST_STATUS.PENDING,
  JOIN_REQUEST_STATUS.APPROVED,
  JOIN_REQUEST_STATUS.REJECTED,
]);
export type JoinRequestStatus = z.infer<typeof joinRequestStatusSchema>;

/**
 * Kết quả gửi join-request (driver UI client — AC1/AC2/AC3):
 *  - 'created': JoinRequest pending mới tạo → toast "Yêu cầu đã gửi, chờ Admin duyệt" (AC1).
 *  - 'already_pending': đã có pending request của user cho nhóm → "đang chờ duyệt" (AC2, KHÔNG tạo dup).
 *  - 'already_member': caller đã là member active → "Vào nhóm" (AC3, KHÔNG tạo request).
 */
export const JOIN_REQUEST_RESULT = {
  CREATED: "created",
  ALREADY_PENDING: "already_pending",
  ALREADY_MEMBER: "already_member",
} as const;

export const joinRequestResultSchema = z.enum([
  JOIN_REQUEST_RESULT.CREATED,
  JOIN_REQUEST_RESULT.ALREADY_PENDING,
  JOIN_REQUEST_RESULT.ALREADY_MEMBER,
]);
export type JoinRequestResult = z.infer<typeof joinRequestResultSchema>;

/** Thông điệp lỗi/UX (một nguồn sự thật api ↔ mobile) — AC1/AC2/AC3/AC4. */
export const JOIN_REQUEST_SENT_MESSAGE =
  "Yêu cầu đã gửi, chờ Admin duyệt.";
export const JOIN_REQUEST_ALREADY_PENDING_MESSAGE =
  "Bạn đã gửi yêu cầu, đang chờ duyệt.";
export const JOIN_REQUEST_ALREADY_MEMBER_MESSAGE =
  "Bạn đã là thành viên.";
export const JOIN_REQUEST_REVOKED_MESSAGE =
  "Link này không còn hiệu lực.";

/**
 * Body cho POST /v1/groups/:id/join-requests (+ /preview).
 * - token TÙY CHỌN (link/QR): khi có → server băm sha256 đối chiếu link Invitation để phát hiện
 *   link bị thu hồi/hết hiệu lực (AC4). Token đi qua BODY (KHÔNG path segment) để KHÔNG lọt vào
 *   access-log `req.url` / ProblemDetail `instance` (bài học H1 PR#44, C2/C3).
 * - `.strict()` chặn field lạ tại biên (over-post: KHÔNG nhận groupId/userId/status từ body — server
 *   quyết; groupId lấy từ path `:id`, userId từ token JWT).
 */
export const joinRequestBodySchema = z
  .object({
    token: z
      .string()
      .trim()
      .min(1, "Token không hợp lệ.")
      .max(JOIN_REQUEST_TOKEN_MAX_LENGTH, "Token không hợp lệ.")
      .optional(),
  })
  .strict();

export type JoinRequestBody = z.infer<typeof joinRequestBodySchema>;

/**
 * Shape response cho POST /v1/groups/:id/join-requests (AC1/AC2/AC3):
 *  - result: created | already_pending | already_member — driver UI client.
 *  - groupId/groupName/avatarUrl: thông tin nhóm để client hiển thị + điều hướng (AC3 "Vào nhóm").
 *  - joinRequestId: id JoinRequest khi result='created'; null cho các trường hợp khác.
 * KHÔNG trả token raw / PII (C2). `.strict()`.
 */
export const joinRequestResponseSchema = z
  .object({
    result: joinRequestResultSchema,
    groupId: z.string().uuid(),
    groupName: z.string(),
    avatarUrl: z.string().nullable(),
    joinRequestId: z.string().uuid().nullable(),
  })
  .strict();

export type JoinRequestResponse = z.infer<typeof joinRequestResponseSchema>;

/**
 * Trạng thái màn preview-join (POST preview) — driver UI client (AC2/AC3/AC4):
 *  - 'joinable': có thể gửi yêu cầu → hiển thị nút "Gửi yêu cầu".
 *  - 'already_pending': đã có pending request → "đang chờ duyệt" (AC2).
 *  - 'already_member': caller đã là member active → "Vào nhóm" (AC3).
 *  - 'revoked': link/QR bị thu hồi/hết hiệu lực → "Link này không còn hiệu lực" (AC4).
 */
export const JOIN_PREVIEW_STATE = {
  JOINABLE: "joinable",
  ALREADY_PENDING: "already_pending",
  ALREADY_MEMBER: "already_member",
  REVOKED: "revoked",
} as const;

export const joinPreviewStateSchema = z.enum([
  JOIN_PREVIEW_STATE.JOINABLE,
  JOIN_PREVIEW_STATE.ALREADY_PENDING,
  JOIN_PREVIEW_STATE.ALREADY_MEMBER,
  JOIN_PREVIEW_STATE.REVOKED,
]);
export type JoinPreviewState = z.infer<typeof joinPreviewStateSchema>;

/**
 * Shape response cho POST preview (màn group-preview trước khi gửi yêu cầu):
 *  - state: joinable | already_pending | already_member | revoked — driver UI.
 *  - groupId/groupName/avatarUrl/memberCount: thông tin nhóm để hiển thị card preview.
 * KHÔNG trả token raw / email / secret (C2). `.strict()`.
 */
export const joinPreviewResponseSchema = z
  .object({
    state: joinPreviewStateSchema,
    groupId: z.string().uuid(),
    groupName: z.string(),
    avatarUrl: z.string().nullable(),
    memberCount: z.number().int().min(0),
  })
  .strict();

export type JoinPreviewResponse = z.infer<typeof joinPreviewResponseSchema>;

// ============================================================
// Review & Approve Join Request — Admin/Owner side
// (US-E4.F4-0026 / @UC0028). GET list pending + approve/reject + bulk.
// ============================================================

/** Max page-size cho list pending join-requests. Default (20) backend áp khi vắng. */
export const JOIN_REQUESTS_LIST_MAX_LIMIT = 50;

/** Độ dài tối đa cursor opaque tại biên (chặn input phình to — DoS). */
export const JOIN_REQUESTS_LIST_MAX_QUERY_LENGTH = 512;

/** Số rid tối đa cho 1 bulk approve/reject (chặn over-post / DoS). */
export const JOIN_REQUEST_BULK_MAX = 100;

/** Thông điệp UX khi duyệt/từ chối (một nguồn sự thật api ↔ mobile) — AC2/AC3. */
export const JOIN_REQUEST_APPROVED_MESSAGE = "Đã duyệt yêu cầu tham gia.";
export const JOIN_REQUEST_REJECTED_MESSAGE = "Đã từ chối yêu cầu tham gia.";

/**
 * Một item trong danh sách pending join-request (AC1 — màn Admin duyệt):
 *  - id: JoinRequest.id (dùng cho approve/reject action).
 *  - userId: User.id của người xin vào.
 *  - fullName: tên thật người xin; null khi chưa đặt.
 *  - avatarUrl: ảnh đại diện; null khi chưa có.
 *  - createdAt: thời điểm gửi yêu cầu (ISO-8601) — sort/hiển thị.
 * `.strict()` chặn rò field PII thừa (email/phone) ra response (C2 — KHÔNG lộ PII thừa).
 */
export const joinRequestListItemSchema = z
  .object({
    id: z.string().uuid(),
    userId: z.string().uuid(),
    fullName: z.string().nullable(),
    avatarUrl: z.string().nullable(),
    createdAt: z.string().datetime(),
  })
  .strict();

export type JoinRequestListItem = z.infer<typeof joinRequestListItemSchema>;

/** Query list pending join-requests: cursor opaque optional, limit ∈ [1,50] optional. */
export const joinRequestsListQuerySchema = z.object({
  cursor: z.string().max(JOIN_REQUESTS_LIST_MAX_QUERY_LENGTH).optional(),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(JOIN_REQUESTS_LIST_MAX_LIMIT)
    .optional(),
});

export type JoinRequestsListQuery = z.infer<typeof joinRequestsListQuerySchema>;

/**
 * Envelope list pending join-requests khớp quy ước repo `{ data, pageInfo }`.
 * KHÔNG lộ email/phone người xin (C2). `.strict()`.
 */
export const joinRequestsListResponseSchema = z
  .object({
    data: z.array(joinRequestListItemSchema),
    pageInfo: z.object({
      hasNext: z.boolean(),
      nextCursor: z.string().nullable(),
    }),
  })
  .strict();

export type JoinRequestsListResponse = z.infer<
  typeof joinRequestsListResponseSchema
>;

/**
 * Kết quả 1 lần approve/reject (AC2/AC3):
 *  - result: 'decided' (vừa chuyển trạng thái) | 'already_decided' (đã approved/rejected trước đó —
 *    idempotent, KHÔNG ghi đè quyết định cũ).
 *  - status: trạng thái cuối của JoinRequest (approved | rejected).
 * `.strict()`.
 */
/**
 * 🔴 US-E4.F2-0597 (review #676 M-2) — mã lỗi ỔN ĐỊNH cho ca "suất đổi trạng thái giữa chừng".
 *
 * `ProblemDetailFilter` chỉ nhấc `code` từ **object**; ném `ConflictException("<câu tiếng Việt>")`
 * rơi về `codeForStatus(409)` ⇒ client **không phân biệt được** với các 409 khác, buộc phải hiển thị
 * `detail` — thứ CLAUDE.md §7 nói rõ là **debug-only, không phải nguồn hiển thị**.
 *
 * Client map `code → message` theo locale người dùng; API không gửi câu đã dịch.
 */
export const JOIN_REQUEST_DECIDE_CODE = {
  /** Suất của người xin vào đổi trạng thái giữa lúc đọc và lúc ghi. Yêu cầu VẪN `pending` ⇒ bấm lại được. */
  SEAT_STATE_CHANGED: "SEAT_STATE_CHANGED",
} as const;

export const SEAT_STATE_CHANGED_MESSAGE =
  "Suất của người này vừa thay đổi trạng thái. Hãy tải lại danh sách rồi thử lại.";

export const JOIN_REQUEST_DECIDE_RESULT = {
  DECIDED: "decided",
  ALREADY_DECIDED: "already_decided",
} as const;

export const joinRequestDecideResultSchema = z.enum([
  JOIN_REQUEST_DECIDE_RESULT.DECIDED,
  JOIN_REQUEST_DECIDE_RESULT.ALREADY_DECIDED,
]);
export type JoinRequestDecideResult = z.infer<
  typeof joinRequestDecideResultSchema
>;

export const joinRequestDecisionResponseSchema = z
  .object({
    result: joinRequestDecideResultSchema,
    status: joinRequestStatusSchema,
  })
  .strict();

export type JoinRequestDecisionResponse = z.infer<
  typeof joinRequestDecisionResponseSchema
>;

/**
 * Body bulk approve/reject (AC4): danh sách rid (1..100), `.strict()` chặn over-post.
 * Server xử lý từng rid độc lập (per-item result) — 1 rid lỗi KHÔNG làm hỏng cả lô.
 */
export const joinRequestBulkBodySchema = z
  .object({
    requestIds: z
      .array(z.string().uuid())
      .min(1, "Cần ít nhất 1 yêu cầu.")
      .max(JOIN_REQUEST_BULK_MAX, "Quá nhiều yêu cầu trong một lần."),
  })
  .strict();

export type JoinRequestBulkBody = z.infer<typeof joinRequestBulkBodySchema>;

/**
 * Kết quả per-item của bulk (AC4):
 *  - id: JoinRequest.id.
 *  - result: 'decided' | 'already_decided' | 'not_found' (rid không thuộc nhóm / không tồn tại).
 *  - status: trạng thái cuối khi xử lý được; null khi not_found.
 */
export const JOIN_REQUEST_BULK_ITEM_RESULT = {
  DECIDED: "decided",
  ALREADY_DECIDED: "already_decided",
  NOT_FOUND: "not_found",
  /**
   * 🔴 US-E4.F2-0597 (review #676 M-3) — suất của người này ĐỔI TRẠNG THÁI giữa lúc đọc và lúc ghi.
   *
   * Mỗi `rid` trong một lô chạy trong TRANSACTION RIÊNG, và docblock của `bulk()` khai rõ *"1 rid
   * lỗi-logic KHÔNG làm hỏng các rid khác"*. Trước giá trị này, `ConflictException` từ `approve()`
   * thoát thẳng ra vòng lặp ⇒ **cả lô 409** trong khi các rid trước ĐÃ COMMIT ⇒ O/A duyệt 10 người,
   * nhận một lỗi, và **không biết ai đã vào nhóm**.
   *
   * Giá trị này giữ đúng bảo đảm per-item: rid đó báo `conflict`, các rid khác vẫn có kết quả riêng.
   */
  CONFLICT: "conflict",
} as const;

export const joinRequestBulkItemResultSchema = z.enum([
  JOIN_REQUEST_BULK_ITEM_RESULT.DECIDED,
  JOIN_REQUEST_BULK_ITEM_RESULT.ALREADY_DECIDED,
  JOIN_REQUEST_BULK_ITEM_RESULT.NOT_FOUND,
  JOIN_REQUEST_BULK_ITEM_RESULT.CONFLICT,
]);
export type JoinRequestBulkItemResult = z.infer<
  typeof joinRequestBulkItemResultSchema
>;

export const joinRequestBulkResultItemSchema = z
  .object({
    id: z.string().uuid(),
    result: joinRequestBulkItemResultSchema,
    status: joinRequestStatusSchema.nullable(),
  })
  .strict();

export type JoinRequestBulkResultItem = z.infer<
  typeof joinRequestBulkResultItemSchema
>;

/** Envelope bulk: per-item results + tổng số đã quyết (decided). `.strict()`. */
export const joinRequestBulkResponseSchema = z
  .object({
    results: z.array(joinRequestBulkResultItemSchema),
    decidedCount: z.number().int().min(0),
  })
  .strict();

export type JoinRequestBulkResponse = z.infer<
  typeof joinRequestBulkResponseSchema
>;

/* ===========================================================================
 * US-E24.F11-0360-S02 (@UC9603) — GET /v1/me/join-requests.
 *
 * VÌ SAO CÓ: cả 7 route join-request hiện có đều là góc nhìn NGƯỜI DUYỆT (O/A) hoặc đòi token.
 * Người xin bấm "Xin vào nhóm", app hiện "đã gửi", rồi im lặng — không biết đang chờ, bị từ chối,
 * hay đã được duyệt. `JoinRequest.userId` đã lưu sẵn người xin; chỉ chưa ai hỏi ngược lại.
 *
 * 🔴 KHÔNG `decidedBy`. Cột có trong DB nhưng đó là DANH TÍNH NGƯỜI TỪ CHỐI. Trả về ⇒ mở đường cho
 * "anh X là người không cho tôi vào nhóm" — mâu thuẫn nguyên tắc "app đóng vai kẻ đòi, không bêu
 * tên". `status` + `decidedAt` đủ cho UI.
 *
 * 🔴 P5.3: `status` là MÃ ỔN ĐỊNH, không phải câu đã dịch. Client map `rejected` thành "Chưa được
 * duyệt" (không phải "Bị từ chối") — backend không quyết giọng điệu.
 * ========================================================================= */

/** Trần limit + độ dài cursor (mirror các list khác của repo). */
export const MY_JOIN_REQUESTS_MAX_LIMIT = 50;
export const MY_JOIN_REQUESTS_MAX_CURSOR_LENGTH = 512;

/** 1 dòng "yêu cầu tôi đã gửi". `.strict()` — thêm `decidedBy` là ĐỎ, không phải cảnh báo. */
export const myJoinRequestListItemSchema = z
  .object({
    requestId: z.string().uuid(),
    groupId: z.string().uuid(),
    groupName: z.string(),
    status: joinRequestStatusSchema,
    /** null khi còn `pending` — KHÔNG bịa mốc thời gian cho một quyết định chưa xảy ra. */
    decidedAt: z.string().nullable(),
    createdAt: z.string(),
  })
  .strict();

export type MyJoinRequestListItem = z.infer<typeof myJoinRequestListItemSchema>;

/** Envelope cursor chuẩn repo. Chưa xin nhóm nào ⇒ `data: []` (KHÔNG 404). */
export const myJoinRequestsListResponseSchema = z
  .object({
    data: z.array(myJoinRequestListItemSchema),
    pageInfo: z
      .object({ hasNext: z.boolean(), nextCursor: z.string().nullable() })
      .strict(),
  })
  .strict();

export type MyJoinRequestsListResponse = z.infer<typeof myJoinRequestsListResponseSchema>;

/** 🔴 `.strict()` là AC (cùng luật S01): `/v1/me/*` bound bằng TOKEN nên 0 tham số định danh. */
export const myJoinRequestsListQuerySchema = z
  .object({
    cursor: z.string().max(MY_JOIN_REQUESTS_MAX_CURSOR_LENGTH).optional(),
    limit: z.coerce.number().int().min(1).max(MY_JOIN_REQUESTS_MAX_LIMIT).optional(),
    status: joinRequestStatusSchema.optional(),
  })
  .strict();

export type MyJoinRequestsListQuery = z.infer<typeof myJoinRequestsListQuerySchema>;
